import { MenuEntity } from "@/firebase";
import {
  URL_EXTERNAL_SECURITY,
  URL_EXTERNAL_DATA_ANALYZER,
  URL_EXTERNAL_DATA_PROTECT,
  URL_EXTERNAL_IG2,
  URL_EXTERNAL_VUZ,
} from "@/env";

export type MenuTabItem = {
  icon: string;
  title: string;
  active: boolean;
  link: string | null;
};
export type MenuCardItem = {
  icon: string;
  title: string;
  active: boolean;
  url: string | null;
  linkList: {
    url: string;
    text: string;
    external: boolean;
  }[];
};

export const menuIcon = (menu: string) => {
  switch (menu) {
    case "home":
      return "home";
    case "security":
      return "security";
    case "dataAnalyzer":
      return "psychology";
    case "dataProtect":
      return "save";
    case "netFinder":
      return "desktop_windows";
    case "vulnTracker":
      return "report";
    case "sysMonitor":
      return "router";
    case "l2Blocker":
      return "balcony";
    case "support":
      return "help";
    case "membership":
      return "people_alt";
    case "joint":
      return "sync";
    case "sso":
      return "cloud";
    default:
      return "";
  }
};

export const tab = (menu: MenuEntity | undefined, admin: boolean) => {
  if (menu == undefined) {
    return null;
  }
  switch (menu.service) {
    case "home":
      return tabHome(menu, admin);
    case "security":
      return tabSecurity(menu, admin);
    case "sase":
      return tabSase(menu, admin);
    case "dataAnalyzer":
      return tabDataAnalyzer(menu, admin);
    case "dataProtect":
      return tabDataProtect(menu, admin);
    case "netFinder":
      return tabNetFinder(menu, admin);
    case "vulnTracker":
      return tabVulnTracker(menu, admin);
    case "sysMonitor":
      return tabSysMonitor(menu, admin);
    case "l2Blocker":
      return tabL2Blocker(menu, admin);
    case "support":
      return tabSupport(menu, admin);
    case "membership":
      return tabMembership(menu, admin);
    case "joint":
      return tabJoint(menu, admin);
    case "sso":
      return tabSso(menu, admin);
  }
  return null;
};
export const card = (menu: MenuEntity | undefined, admin: boolean) => {
  if (menu == undefined) {
    return null;
  }
  switch (menu.service) {
    case "security":
      return cardSecurity(menu, admin);
    case "sase":
      return cardSase(menu, admin);
    case "dataAnalyzer":
      return cardDataAnalyzer(menu, admin);
    case "dataProtect":
      return cardDataProtect(menu, admin);
    case "netFinder":
      return cardNetFinder(menu, admin);
    case "vulnTracker":
      return cardVulnTracker(menu, admin);
    case "sysMonitor":
      return cardSysMonitor(menu, admin);
    case "l2Blocker":
      return cardL2Blocker(menu, admin);
    case "support":
      return cardSupport(menu, admin);
    case "membership":
      return cardMembership(menu, admin);
    case "joint":
      return cardJoint(menu, admin);
    case "sso":
      return cardSso(menu, admin);
  }
  return null;
};

// 共通
const externalText = "サービス内容はこちら";
// Home（タブのみ）
const titleHome = "Home";
const urlHome = "/home";
// eslint-disable-next-line
const tabHome = (menu: MenuEntity, admin: boolean) => {
  return {
    icon: menuIcon("home"),
    title: menu.alt ? menu.alt : titleHome,
    active: menu.active,
    link: menu.active ? urlHome : null,
  };
};
// Managed Security
const titleSecurity = "VSR n Series";
const titleSase = "Vario SASE";
const titleSecurityShort = "VSR";
const urlSecurity = "/security";
// securityとsaseのMenuInfoから、securityとして適用するMenuInfoを生成
export const mergeSecurityMenu = (
  menuSecurity: MenuEntity | undefined,
  menuSase: MenuEntity | undefined,
): MenuEntity | undefined => {
  if (menuSecurity && menuSase) {
    // securityとsaseの両方が登録されている
    if (menuSecurity.active && menuSase.active) {
      // securityとsaseの両方がactive
      const title1 = menuSase.alt ? menuSase.alt : titleSase;
      const title2 = menuSecurity.alt ? menuSecurity.alt : titleSecurityShort;
      // altに表示名をセットし、サービスはsecurityとする
      return {
        service: menuSecurity.service,
        alt: title1 + "/" + title2,
        active: true,
      };
    } else if (menuSase.active) {
      // saseがactive（かつsecurityはactiveではない）
      return menuSase;
    }
    // securtiyのみactiveか、どちらもactiveでない
    return menuSecurity;
  }
  // どちらか一方が登録されているか、どちらも登録されていない
  return menuSecurity ? menuSecurity : menuSase;
};
const tabSecurity = (menu: MenuEntity, admin: boolean) => {
  if (!admin) {
    return null;
  }
  return {
    icon: menuIcon("security"),
    title: menu.alt ? menu.alt : titleSecurity,
    active: menu.active,
    link: menu.active ? urlSecurity : URL_EXTERNAL_SECURITY,
  };
};
const tabSase = (menu: MenuEntity, admin: boolean) => {
  if (!admin) {
    return null;
  }
  return {
    icon: menuIcon("security"),
    title: menu.alt ? menu.alt : titleSase,
    active: menu.active,
    link: menu.active ? urlSecurity : URL_EXTERNAL_VUZ,
  };
};
const cardSecurity = (menu: MenuEntity, admin: boolean) => {
  if (!admin) {
    return null;
  }
  return {
    icon: menuIcon("security"),
    title: menu.alt ? menu.alt : titleSecurity,
    active: menu.active,
    url: menu.active ? urlSecurity : null,
    linkList: menu.active
      ? []
      : [
          {
            url: URL_EXTERNAL_SECURITY,
            text: externalText,
            external: true,
          },
        ],
  };
};
const cardSase = (menu: MenuEntity, admin: boolean) => {
  if (!admin) {
    return null;
  }
  return {
    icon: menuIcon("security"),
    title: menu.alt ? menu.alt : titleSase,
    active: menu.active,
    url: menu.active ? urlSecurity : null,
    linkList: menu.active
      ? []
      : [
          {
            url: URL_EXTERNAL_VUZ,
            text: externalText,
            external: true,
          },
        ],
  };
};
// Data Analyzer
const titleDataAnalyzer = "Kishin";
const urlDataAnalyzer = "/data-analyzer";
const tabDataAnalyzer = (menu: MenuEntity, admin: boolean) => {
  if (!admin) {
    return null;
  }
  return {
    icon: menuIcon("dataAnalyzer"),
    title: menu.alt ? menu.alt : titleDataAnalyzer,
    active: menu.active,
    link: menu.active ? urlDataAnalyzer : URL_EXTERNAL_DATA_ANALYZER,
  };
};
const cardDataAnalyzer = (menu: MenuEntity, admin: boolean) => {
  if (!admin) {
    return null;
  }
  return {
    icon: menuIcon("dataAnalyzer"),
    title: menu.alt ? menu.alt : titleDataAnalyzer,
    active: menu.active,
    url: menu.active ? urlDataAnalyzer : null,
    linkList: menu.active
      ? []
      : [
          {
            url: URL_EXTERNAL_DATA_ANALYZER,
            text: externalText,
            external: true,
          },
        ],
  };
};
// Data Protect
const titleDataProtect = "Data Protect";
const urlDataProtect = "/data-protect";
const tabDataProtect = (menu: MenuEntity, admin: boolean) => {
  if (!admin) {
    return null;
  }
  return {
    icon: menuIcon("dataProtect"),
    title: menu.alt ? menu.alt : titleDataProtect,
    active: menu.active,
    link: menu.active ? urlDataProtect : URL_EXTERNAL_DATA_PROTECT,
  };
};
const cardDataProtect = (menu: MenuEntity, admin: boolean) => {
  if (!admin) {
    return null;
  }
  return {
    icon: menuIcon("dataProtect"),
    title: menu.alt ? menu.alt : titleDataProtect,
    active: menu.active,
    url: menu.active ? urlDataProtect : null,
    linkList: menu.active
      ? []
      : [
          {
            url: URL_EXTERNAL_DATA_PROTECT,
            text: externalText,
            external: true,
          },
        ],
  };
};
// Network Finder
const titleNetFinder = "Network Finder";
const urlNetFinder = "/net-finder";
// eslint-disable-next-line
const tabNetFinder = (menu: MenuEntity, admin: boolean) => {
  return {
    icon: menuIcon("netFinder"),
    title: menu.alt ? menu.alt : titleNetFinder,
    active: menu.active,
    link: menu.active ? urlNetFinder : null,
  };
};
const cardNetFinder = (menu: MenuEntity, admin: boolean) => {
  return {
    icon: menuIcon("netFinder"),
    title: menu.alt ? menu.alt : titleNetFinder,
    active: menu.active,
    url: menu.active ? urlNetFinder : null,
    linkList: admin
      ? [
          {
            url: "/net-finder/dashboard",
            text: "ダッシュボード",
            external: false,
          },
          {
            url: "/net-finder/network",
            text: "スキャングループ",
            external: false,
          },
          { url: "/net-finder/v-sensor", text: "スキャナ", external: false },
          {
            url: "/net-finder/managed-node",
            text: "発見端末一覧",
            external: false,
          },
          {
            url: "/net-finder/asset-management",
            text: "アップロード管理",
            external: false,
          },
        ]
      : [
          {
            url: "/net-finder/dashboard",
            text: "ダッシュボード",
            external: false,
          },
          {
            url: "/net-finder/managed-node",
            text: "発見端末一覧",
            external: false,
          },
        ],
  };
};
// Vulnerability Tracker
const titleVulnTracker = "Vulnerability Tracker";
const urlVulnTracker = "/vuln-tracker";
const tabVulnTracker = (menu: MenuEntity, admin: boolean) => {
  if (!admin) {
    return null;
  }
  return {
    icon: menuIcon("vulnTracker"),
    title: menu.alt ? menu.alt : titleVulnTracker,
    active: menu.active,
    link: menu.active ? urlVulnTracker : null,
  };
};
const cardVulnTracker = (menu: MenuEntity, admin: boolean) => {
  if (!admin) {
    return null;
  }
  return {
    icon: menuIcon("vulnTracker"),
    title: menu.alt ? menu.alt : titleVulnTracker,
    active: menu.active,
    url: menu.active ? urlVulnTracker : null,
    linkList: [
      { url: "/vuln-tracker/top", text: "新着情報", external: false },
      {
        url: "/vuln-tracker/vulnerability",
        text: "Microsoft",
        external: false,
      },
      {
        url: "/vuln-tracker/vulnerability-redhat",
        text: "Red Hat",
        external: false,
      },
      {
        url: "/vuln-tracker/detected-node",
        text: "検知端末一覧",
        external: false,
      },
      {
        url: "/vuln-tracker/windows-update",
        text: "Windows Update",
        external: false,
      },
    ],
  };
};
// System Monitor
const titleSysMonitor = "System Monitor";
const urlSysMonitor = "/sys-monitor";
const tabSysMonitor = (menu: MenuEntity, admin: boolean) => {
  if (!admin) {
    return null;
  }
  return {
    icon: menuIcon("sysMonitor"),
    title: menu.alt ? menu.alt : titleSysMonitor,
    active: menu.active,
    link: menu.active ? urlSysMonitor : null,
  };
};
const cardSysMonitor = (menu: MenuEntity, admin: boolean) => {
  if (!admin) {
    return null;
  }
  return {
    icon: menuIcon("sysMonitor"),
    title: menu.alt ? menu.alt : titleSysMonitor,
    active: menu.active,
    url: menu.active ? urlSysMonitor : null,
    linkList: [],
  };
};
// IntraGuardian2
const titleL2Blocker = "IntraGuardian2";
const urlL2Blocker = "/l2-blocker";
const tabL2Blocker = (menu: MenuEntity, admin: boolean) => {
  if (!admin) {
    return null;
  }
  return {
    icon: menuIcon("l2Blocker"),
    title: menu.alt ? menu.alt : titleL2Blocker,
    active: menu.active,
    link: menu.active ? urlL2Blocker : null,
  };
};
const cardL2Blocker = (menu: MenuEntity, admin: boolean) => {
  if (!admin) {
    return null;
  }
  return {
    icon: menuIcon("l2Blocker"),
    title: menu.alt ? menu.alt : titleL2Blocker,
    active: menu.active,
    url: menu.active ? urlL2Blocker : null,
    linkList: menu.active
      ? []
      : [{ url: URL_EXTERNAL_IG2, text: "(開発中)", external: true }],
  };
};
// Support
const titleSupport = "サポート";
const urlSupport = "/support";
// eslint-disable-next-line
const tabSupport = (menu: MenuEntity, admin: boolean) => {
  return {
    icon: menuIcon("support"),
    title: menu.alt ? menu.alt : titleSupport,
    active: menu.active,
    link: menu.active ? urlSupport : null,
  };
};
// eslint-disable-next-line
const cardSupport = (menu: MenuEntity, admin: boolean) => {
  return {
    icon: menuIcon("support"),
    title: menu.alt ? menu.alt : titleSupport,
    active: menu.active,
    url: menu.active ? urlSupport : null,
    linkList: [
      { url: "/support/download", text: "ダウンロード", external: false },
      { url: "/support/inquiry", text: "関連情報", external: false },
    ],
  };
};

// membership
const titleMembership = "membership";
const urlMembership = "/membership";
// eslint-disable-next-line
const tabMembership = (menu: MenuEntity, admin: boolean) => {
  return {
    icon: menuIcon("membership"),
    title: menu.alt ? menu.alt : titleMembership,
    active: menu.active,
    link: menu.active ? urlMembership : null,
  };
};
const cardMembership = (menu: MenuEntity, admin: boolean) => {
  return {
    icon: menuIcon("membership"),
    title: menu.alt ? menu.alt : titleMembership,
    active: menu.active,
    url: menu.active ? urlMembership : null,
    linkList: admin
      ? [
          { url: "/membership/employee", text: "社員一覧", external: false },
          { url: "/membership/account", text: "ユーザー管理", external: false },
        ]
      : [{ url: "/membership/employee", text: "社員一覧", external: false }],
  };
};
// JoInT
const titleJoint = "Joint";
const urlJoint = "/joint";
const tabJoint = (menu: MenuEntity, admin: boolean) => {
  if (!admin) {
    return null;
  }
  return {
    icon: menuIcon("joint"),
    title: menu.alt ? menu.alt : titleJoint,
    active: menu.active,
    link: menu.active ? urlJoint : null,
  };
};
const cardJoint = (menu: MenuEntity, admin: boolean) => {
  if (!admin) {
    return null;
  }
  return {
    icon: menuIcon("joint"),
    title: menu.alt ? menu.alt : titleJoint,
    active: menu.active,
    url: menu.active ? urlJoint : null,
    linkList: [
      { url: "/joint/ad-get-users", text: "ADサーバ", external: false },
    ],
  };
};
// Vario SSO
const titleSso = "Vario SSO";
const urlSso = "/sso";
const tabSso = (menu: MenuEntity, admin: boolean) => {
  if (!admin) {
    return null;
  }
  return {
    icon: menuIcon("sso"),
    title: menu.alt ? menu.alt : titleSso,
    active: menu.active,
    link: menu.active ? urlSso : URL_EXTERNAL_VUZ,
  };
};
const cardSso = (menu: MenuEntity, admin: boolean) => {
  if (!admin) {
    return null;
  }
  // SSOは、メニュー非活性時にカードを表示しない（タイトルのみ）
  if (!menu.active) {
    return null;
  }
  return {
    icon: menuIcon("sso"),
    title: menu.alt ? menu.alt : titleSso,
    active: menu.active,
    url: menu.active ? urlSso : null,
    linkList: [
      { url: "/sso/", text: "ステータス", external: false },
      { url: "/sso/user", text: "ユーザー情報一覧", external: false },
      { url: "/sso/audit-log", text: "ログ", external: false },
      { url: "/sso/download", text: "ダウンロード", external: false },
      { url: "/sso/inquiry", text: "お問い合わせ", external: false },
    ],
  };
};
